// frontend/app/components/lessonPlans/present/presenterWindowDoc.js
//
// The whole presenter window, as one self-contained HTML document.
//
// WHY PLAIN HTML AND NOT REACT. This app is React Native Web, and RNW injects
// the styles a component needs into the *opener's* document as it renders them.
// A popup is a different document: those style sheets are not in it and never
// will be, so a React Native tree portalled into a popup arrives with no
// layout, no colours and no type — a window that looks broken and gives no clue
// why. DavinciCertificate.js already solved this the honest way: open a blank
// window, write a document that carries its own CSS, and be done. This is the
// same move, with a script instead of a print button.
//
// WHY THE STATE IS SEEDED INTO THE DOCUMENT. A presenter window is opened when
// the teacher realises they want it, which is rarely on step 1. It renders from
// a seed on first paint and from channel messages after that, through the same
// render function — so the window opened mid-lesson shows the step that is
// actually on the wall, and a browser with no BroadcastChannel still gets a
// useful, honest snapshot instead of an empty frame.
//
// WHY THE POPUP BINDS THE CLICKER KEYS. The clicker's receiver is plugged into
// the laptop, and this window lives on the laptop. Whichever window has focus
// receives the keypress, so both have to answer it the same way — see
// PRESENTER_INTENTS in presenterChannel.js.

import { PRESENTER_CHANNEL_NAME, PRESENTER_MESSAGE } from "./presenterChannel";

/**
 * Dark by default, because the room's lights are usually down and a white
 * laptop screen lights the teacher's face up in front of the class. It still
 * follows the app's own light mode when that is what the teacher chose — this
 * window is theirs, and surprising them with a theme they did not pick is the
 * kind of small wrongness that makes a tool feel untrustworthy.
 *
 * Every size here is a laptop-at-arm's-length size, deliberately NOT present
 * mode's back-of-the-room scale. Nobody is reading this from six metres away,
 * and blowing the guidance up to projector size would fit two sentences on the
 * screen and hide the rest.
 */
const PRESENTER_STYLES = `
  * { box-sizing: border-box; }
  html, body { height: 100%; }
  body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    background: var(--bg);
    color: var(--text);
    display: flex;
    flex-direction: column;
    -webkit-font-smoothing: antialiased;
  }
  body.dark {
    --bg: #0b1220;
    --panel: #111c31;
    --text: #f8fafc;
    --muted: #94a3b8;
    --border: rgba(148,163,184,0.28);
    --note-bg: rgba(251,191,36,0.10);
    --note-border: rgba(251,191,36,0.34);
    --note-label: #fde68a;
    --btn-bg: rgba(148,163,184,0.10);
    --accent: #38bdf8;
    --warn-bg: rgba(248,113,113,0.14);
    --warn-border: rgba(248,113,113,0.45);
    --warn-text: #fca5a5;
  }
  body.light {
    --bg: #ffffff;
    --panel: #f8fafc;
    --text: #0f172a;
    --muted: #475569;
    --border: #cbd5e1;
    --note-bg: #fffbeb;
    --note-border: #fde68a;
    --note-label: #92400e;
    --btn-bg: #f1f5f9;
    --accent: #0077b6;
    --warn-bg: #fef2f2;
    --warn-border: #fecaca;
    --warn-text: #b91c1c;
  }

  .banner {
    display: none;
    padding: 10px 20px;
    font-size: 13px;
    font-weight: 600;
    background: var(--warn-bg);
    border-bottom: 1px solid var(--warn-border);
    color: var(--warn-text);
  }
  .banner.show { display: block; }

  .bar {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 20px;
    border-bottom: 1px solid var(--border);
    background: var(--panel);
    flex: 0 0 auto;
  }
  .lesson-title {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.4px;
    color: var(--muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1 1 auto;
  }
  .counter {
    font-size: 13px;
    font-weight: 700;
    color: var(--muted);
    flex: 0 0 auto;
  }
  /* Tabular figures so the clock does not jitter sideways every second in the
     corner of the teacher's eye. */
  .timer {
    font-size: 20px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    letter-spacing: 1px;
    flex: 0 0 auto;
    min-width: 76px;
    text-align: right;
  }

  .body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 22px 24px 8px;
  }
  .phase-row { display: flex; align-items: center; gap: 9px; margin-bottom: 8px; }
  .dot { width: 10px; height: 10px; border-radius: 50%; flex: 0 0 auto; }
  .phase {
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 1.3px;
    text-transform: uppercase;
  }
  .minutes { font-size: 12px; font-weight: 600; color: var(--muted); }

  .step-title {
    margin: 0 0 18px;
    font-size: 26px;
    line-height: 1.25;
    font-weight: 800;
  }

  .note {
    background: var(--note-bg);
    border: 1px solid var(--note-border);
    border-radius: 12px;
    padding: 12px 16px;
    margin: 0 0 12px;
  }
  .note-label {
    margin: 0 0 5px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 1.1px;
    text-transform: uppercase;
    color: var(--note-label);
  }
  .note-text { margin: 0; font-size: 16px; line-height: 1.5; white-space: pre-wrap; }
  .note-empty { margin: 0; font-size: 15px; color: var(--muted); font-style: italic; }

  .next {
    margin-top: 20px;
    padding: 12px 16px;
    border: 1px dashed var(--border);
    border-radius: 12px;
    background: var(--panel);
  }
  .next-label {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 5px;
  }
  .next-phase { font-size: 11px; font-weight: 800; letter-spacing: 1.1px; text-transform: uppercase; }
  .next-title { font-size: 16px; font-weight: 700; margin-top: 2px; }

  .controls {
    flex: 0 0 auto;
    border-top: 1px solid var(--border);
    background: var(--panel);
    padding: 12px 20px 14px;
  }
  .controls.hidden { display: none; }
  .buttons { display: flex; align-items: center; gap: 10px; }
  .btn {
    font: inherit;
    font-size: 15px;
    font-weight: 700;
    color: var(--text);
    background: var(--btn-bg);
    border: 1px solid var(--border);
    border-radius: 999px;
    padding: 11px 20px;
    cursor: pointer;
  }
  .btn:disabled { opacity: 0.38; cursor: default; }
  .btn:not(:disabled):hover { border-color: var(--accent); }
  /* Reveal is the one control with a consequence the room sees, so it is the
     only one that carries colour. */
  .btn-reveal {
    border-color: var(--accent);
    color: var(--accent);
    flex: 1 1 auto;
  }
  .btn-reveal:disabled { border-color: var(--border); color: var(--text); }
  .status { margin-top: 9px; font-size: 12px; font-weight: 600; color: var(--muted); }
  .keys { margin-top: 4px; font-size: 11px; color: var(--muted); }
`;

/**
 * The popup's own logic. Written as an ES5-ish string on purpose: it is not
 * transpiled, it is not linted, and it runs in whatever the teacher's school
 * has installed. No template literals in here — this whole thing lives inside
 * one in the module below.
 */
const PRESENTER_SCRIPT = `
(function () {
  var seedNode = document.getElementById("presenter-seed");
  var state = null;
  try { state = JSON.parse(seedNode.textContent); } catch (err) { state = null; }
  if (!state) return;

  var sessionId = state.sessionId;
  var MSG = state.messages;
  var channel = null;
  // Whether there was a lesson window when this one loaded. The watchdog at the
  // bottom only runs if there was: a browser that never handed us an opener
  // must not be read as "the lesson has gone" and close the teacher's notes two
  // seconds after they opened them.
  var hadOpener = !!window.opener;

  function el(id) { return document.getElementById(id); }
  function setText(id, value) {
    var node = el(id);
    if (node) node.textContent = value == null ? "" : String(value);
  }
  function words() { return (state && state.labels) || {}; }
  function fill(template, values) {
    // Doubled backslashes on purpose: this whole script is a template literal
    // in the module below, which resolves escapes before the browser ever sees
    // them. Written singly, this regex reaches the popup as /{(w+)}/ and every
    // placeholder renders as its own name.
    return String(template == null ? "" : template).replace(
      /\\{(\\w+)\\}/g,
      function (whole, key) {
        return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : whole;
      }
    );
  }

  function noteSection(label, text) {
    var wrap = document.createElement("section");
    wrap.className = "note";
    var head = document.createElement("h2");
    head.className = "note-label";
    head.textContent = label;
    var body = document.createElement("p");
    body.className = "note-text";
    body.textContent = text;
    wrap.appendChild(head);
    wrap.appendChild(body);
    return wrap;
  }

  function renderNotes(notes) {
    var host = el("notes");
    if (!host) return;
    var L = words();
    while (host.firstChild) host.removeChild(host.firstChild);
    var wrote = false;
    if (notes && notes.guidance) {
      host.appendChild(noteSection(L.whatYouDo, notes.guidance));
      wrote = true;
    }
    if (notes && notes.studentActivity) {
      host.appendChild(noteSection(L.withClass, notes.studentActivity));
      wrote = true;
    }
    var tips = (notes && notes.tips) || [];
    for (var i = 0; i < tips.length; i++) {
      if (tips[i] && tips[i].text) {
        host.appendChild(noteSection(L.teacherTip, tips[i].text));
        wrote = true;
      }
    }
    if (!wrote) {
      var empty = document.createElement("p");
      empty.className = "note-empty";
      empty.textContent = L.noNotes;
      host.appendChild(empty);
    }
  }

  function clock(ms) {
    var total = Math.max(0, Math.floor(ms / 1000));
    var hours = Math.floor(total / 3600);
    var minutes = Math.floor((total % 3600) / 60);
    var seconds = total % 60;
    var mm = hours > 0 && minutes < 10 ? "0" + minutes : String(minutes);
    var ss = seconds < 10 ? "0" + seconds : String(seconds);
    return (hours > 0 ? hours + ":" : "") + mm + ":" + ss;
  }

  function tickTimer() {
    if (!state || !state.startedAt) { setText("timer", "--:--"); return; }
    setText("timer", clock(Date.now() - state.startedAt));
  }

  function render() {
    var L = words();
    document.body.className = state.theme === "light" ? "light" : "dark";
    document.title = state.lessonTitle
      ? L.presenterTitle + " — " + state.lessonTitle
      : L.presenterTitle;

    setText("lesson-title", state.lessonTitle);
    setText(
      "counter",
      state.total > 0
        ? fill(L.step, { n: state.stepNumber, total: state.total })
        : ""
    );

    var dot = el("phase-dot");
    if (dot) dot.style.backgroundColor = state.phaseAccent;
    var phase = el("phase");
    if (phase) {
      phase.textContent = state.phaseLabel;
      phase.style.color = state.phaseAccent;
    }
    setText(
      "minutes",
      state.estimatedMinutes ? "· " + state.estimatedMinutes + " " + L.minutes : ""
    );
    setText("step-title", state.stepTitle);

    renderNotes(state.notes);

    var nextPhase = el("next-phase");
    if (state.next) {
      setText("next-label", L.comingNext);
      if (nextPhase) {
        nextPhase.textContent = state.next.phaseLabel;
        nextPhase.style.color = state.next.phaseAccent;
      }
      setText(
        "next-title",
        state.next.title +
          (state.next.estimatedMinutes
            ? "  ·  " + state.next.estimatedMinutes + " " + L.minutes
            : "")
      );
    } else {
      setText("next-label", L.lastStep);
      if (nextPhase) { nextPhase.textContent = ""; }
      setText("next-title", L.endOfLesson);
    }

    var prev = el("prev");
    var next = el("next");
    var reveal = el("reveal");
    // The arrows are drawn here rather than in the markup so the label a
    // translator supplies keeps its direction indicator without having to carry
    // the character itself.
    if (prev) {
      prev.disabled = !!state.atStart;
      prev.textContent = "← " + L.back;
    }
    if (next) {
      next.disabled = !!state.atEnd;
      next.textContent = L.next + " →";
    }
    if (reveal) {
      reveal.disabled = state.revealsLeft <= 0;
      reveal.textContent = state.revealsLeft > 1
        ? L.reveal + " (" + state.revealsLeft + ")"
        : L.reveal;
    }

    setText(
      "status",
      state.revealTotal === 0
        ? L.nothingToReveal
        : fill(L.revealed, { shown: state.revealCount, total: state.revealTotal })
    );
    setText("keys", L.keys);

    tickTimer();
  }

  function sendIntent(action) {
    if (!channel) return;
    try {
      channel.postMessage({ type: MSG.INTENT, sessionId: sessionId, action: action });
    } catch (err) { /* the lesson window has gone; the poll below notices */ }
  }

  function bindButton(id, action) {
    var node = el(id);
    if (!node) return;
    node.addEventListener("click", function () {
      if (node.disabled) return;
      sendIntent(action);
    });
  }

  bindButton("prev", "previous");
  bindButton("next", "next");
  bindButton("reveal", "reveal");

  // The clicker again: its receiver is in this laptop, so these keys land here
  // whenever this window has focus, and they have to mean exactly what they
  // mean on the lesson window. Escape is the one that differs — it closes this
  // window only. A teacher dismissing their own notes must never be the gesture
  // that ends the lesson in front of the class.
  document.addEventListener("keydown", function (event) {
    if (event.defaultPrevented) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    var key = event.key;
    if (key === "ArrowLeft" || key === "PageUp") {
      event.preventDefault(); sendIntent("previous"); return;
    }
    if (key === "ArrowRight" || key === "PageDown") {
      event.preventDefault(); sendIntent("next"); return;
    }
    if (key === " " || key === "Spacebar" || key === "Enter") {
      // A button under the caret already answers Space itself; letting both
      // fire would spend two beats on one press.
      var tag = event.target && event.target.tagName;
      if (tag === "BUTTON" || tag === "A") return;
      event.preventDefault(); sendIntent("advance"); return;
    }
    if (key === "Escape") { event.preventDefault(); window.close(); }
  });

  function goOffline(message) {
    var banner = el("banner");
    if (banner) {
      banner.textContent = message;
      banner.className = "banner show";
    }
    var controls = el("controls");
    if (controls) controls.className = "controls hidden";
  }

  try {
    channel = new window.BroadcastChannel(state.channelName);
  } catch (err) {
    channel = null;
  }

  if (channel) {
    channel.onmessage = function (event) {
      var data = event && event.data;
      if (!data || typeof data !== "object" || data.sessionId !== sessionId) return;
      if (data.type === MSG.STATE && data.state) {
        state = data.state;
        render();
        return;
      }
      if (data.type === MSG.ENDED) {
        // The lesson is over. An orphaned window still showing its notes is a
        // window a teacher will trust at the start of the next lesson.
        window.close();
      }
    };
    // The lesson window has no idea this one exists until it is told. Without
    // this, a presenter window opened on step 9 would sit on its seed until the
    // teacher happened to change something.
    try {
      channel.postMessage({ type: MSG.HELLO, sessionId: sessionId });
    } catch (err) { /* nothing to do; the seed is already on screen */ }

    window.addEventListener("beforeunload", function () {
      try {
        channel.postMessage({ type: MSG.BYE, sessionId: sessionId });
      } catch (err) { /* going away anyway */ }
    });
  } else {
    goOffline(words().offline);
  }

  render();
  setInterval(tickTimer, 1000);

  // The backstop for an abnormal death: the lesson tab crashed, or was closed
  // by the OS, so no ENDED ever arrives. Checking the opener is the only signal
  // left, and a presenter window outliving its lesson is the exact failure this
  // whole feature must not introduce.
  if (hadOpener) {
    setInterval(function () {
      if (!window.opener || window.opener.closed) window.close();
    }, 2000);
  }
})();
`;

/**
 * JSON safe to drop inside a script element.
 *
 * The only sequence that matters is a literal "</script>" appearing inside an
 * authored string — a lesson step titled "Why </script> breaks a page" is
 * unlikely but perfectly legal, and it would end the element early and spray
 * the rest of the lesson into the document as markup. Escaping every "<" costs
 * nothing and closes the hole completely. Nothing else needs escaping here:
 * every dynamic value in the window is written with textContent, never innerHTML.
 */
export const presenterSeedJson = (value) =>
  JSON.stringify(value).replace(/</g, "\\u003c");

/**
 * The complete document for the presenter window.
 *
 * The markup is a fixed shell with empty slots; the script fills every one of
 * them from the seed on load. That is why there is no HTML escaping in this
 * function — no lesson text is ever concatenated into markup.
 */
export const buildPresenterDoc = (state, { channelName = PRESENTER_CHANNEL_NAME } = {}) => {
  const seed = presenterSeedJson({
    ...state,
    channelName,
    messages: PRESENTER_MESSAGE,
  });

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Presenter view</title>
<style>${PRESENTER_STYLES}</style>
</head>
<body class="dark">
<div id="banner" class="banner"></div>

<header class="bar">
  <span id="lesson-title" class="lesson-title"></span>
  <span id="counter" class="counter"></span>
  <span id="timer" class="timer">0:00</span>
</header>

<main class="body">
  <div class="phase-row">
    <span id="phase-dot" class="dot"></span>
    <span id="phase" class="phase"></span>
    <span id="minutes" class="minutes"></span>
  </div>
  <h1 id="step-title" class="step-title"></h1>

  <div id="notes"></div>

  <section class="next">
    <div id="next-label" class="next-label">Coming next</div>
    <div id="next-phase" class="next-phase"></div>
    <div id="next-title" class="next-title"></div>
  </section>
</main>

<footer id="controls" class="controls">
  <div class="buttons">
    <button id="prev" class="btn" type="button"></button>
    <button id="reveal" class="btn btn-reveal" type="button"></button>
    <button id="next" class="btn" type="button"></button>
  </div>
  <div id="status" class="status"></div>
  <div id="keys" class="keys"></div>
</footer>

<script id="presenter-seed" type="application/json">${seed}</script>
<script>${PRESENTER_SCRIPT}</script>
</body>
</html>`;
};

export default buildPresenterDoc;
